import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { SendVerificationEmail } from "../redux/auth-reducer/auth-service";
import {
  EMAIL_VERIFICATION_EVENT,
  readApiMessage,
  readStoredEmailVerified,
  setStoredEmailVerified,
} from "../utils/helper/helper";

/**
 * Whether the signed-in account's email is verified, as stored in "isUser", following every change:
 * from this tab (setStoredEmailVerified's EMAIL_VERIFICATION_EVENT) and from another one (the
 * storage event), such as the /VerifyEmail page opened from the email in a new tab.
 *
 * @returns {boolean|null} null while it is not known.
 */
export const useEmailVerified = () => {
  const [verified, setVerified] = useState(readStoredEmailVerified);

  useEffect(() => {
    const sync = () => setVerified(readStoredEmailVerified());
    window.addEventListener(EMAIL_VERIFICATION_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EMAIL_VERIFICATION_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return verified;
};

/**
 * Emails the signed-in account a new verification link (Account/SendVerificationEmail) and says how
 * that went: the address it went to, or why not - a link sent a moment ago, the day's limit. An
 * account that has verified meanwhile is told so, and its stored flag follows.
 *
 * @returns {{send: () => Promise<void>, sending: boolean}}
 */
export const useSendVerificationEmail = () => {
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    const res = await SendVerificationEmail();
    setSending(false);

    if (res?.data?.emailVerified === true) {
      setStoredEmailVerified(true);
      toast.info(res.data.message);
    } else if (res?.data?.status === true) {
      toast.success(res.data.message);
    } else if (res) {
      toast.error(readApiMessage(res));
    }
  };

  return { send, sending };
};
